import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useStableSearchParams } from '../hooks/useStableSearchParams';
import { 
  LayoutDashboard, 
  ShoppingCart, 
  MessageSquare, 
  Package, 
  Users,
  DollarSign,
  TrendingUp,
  Clock,
  Loader2,
  LogOut,
  ChevronRight,
  ChevronDown,
  ChevronLeft,
  Plus,
  Pencil,
  Trash2,
  X,
  Menu,
  Search,
  Image as ImageIcon,
  FileText,
  Download,
  Eye,
  Tag,
  Megaphone,
  Factory,
  Calculator,
  Settings,
  Database,
  Percent,
  Truck,
  FolderTree,
  ClipboardList,
  Layers,
  Boxes,
  Shield,
  Lock,
  Upload,
  Activity,
  Bell,
  Mail,
  UserX,
  ArrowLeftCircle,
  Building,
  BarChart3,
  LayoutTemplate,
  BookOpen,
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { useAuth } from '../context/AuthContext';
import { adminAPI, authAPI, smallFormatAPI, largeFormatAPI, swagAPI, awardsAPI, crmAPI } from '../utils/api';
import { toast } from 'sonner';
import AdminPromotions from './Admin/AdminPromotions';
import AdminHeroSlider from './Admin/AdminHeroSlider';
import AdminPricing from './Admin/AdminPricing';
import AdminProductImages from './Admin/AdminProductImages';
import AdminProductsPage from './Admin/AdminProductsPage';
import AdminCategories from './Admin/AdminCategories';
import AdminProductTypes from './Admin/AdminProductTypes';
// CRM/Operations imports
import AdminLeads from './Admin/AdminLeads';
import AdminTasks from './Admin/AdminTasks';
import AdminActivities from './Admin/AdminActivities';
// AdminQuotes REMOVED - quotes are now on orders
import AdminWorkOrders from './Admin/AdminWorkOrders';
import AdminRoleManagement from './Admin/AdminRoleManagement';
import AdminRoleAuditLog from './Admin/AdminRoleAuditLog';
import AdminOrderDetailsPage from './Admin/AdminOrderDetails';
import CustomerDetailPage from './Admin/CustomerDetailPage';
import ContactDetailPage from './Admin/ContactDetailPage';
import LeadDetailPage from './Admin/LeadDetailPage';
import TaskDetailPage from './Admin/TaskDetailPage';
// QuoteDetailPage REMOVED - quotes are now on orders
import OrderDetailPage from './Admin/OrderDetailPageClean';  // P1 Clean 3-Panel Layout
import WorkOrderDetailPage from './Admin/WorkOrderDetailPage';
import ArtworkProofDetailPage from './Admin/ArtworkProofDetailPage';
// Training Documentation Viewer
import DocsViewer from './Admin/DocsViewer';
// InvoiceDetailPage REMOVED - invoices are now on orders
// Enhanced list views (standardized tables)
import AdminOrdersEnhanced from './Admin/AdminOrdersEnhanced';
// AdminInvoicesEnhanced REMOVED - invoices are now on orders (use Orders with Invoice Issued filter)
import AdminPurchaseOrdersEnhanced from './Admin/AdminPurchaseOrdersEnhanced';
import AdminProductsEnhanced from './Admin/AdminProductsEnhanced';
import AdminManufacturersEnhanced from './Admin/AdminManufacturersEnhanced';
import AdminAccountsEnhanced from './Admin/AdminAccountsEnhanced';
import AdminContactsEnhanced from './Admin/AdminContactsEnhanced';  // CRM Contacts
import AdminBillsEnhanced from './Admin/AdminBillsEnhanced';
// Expenses module imports
import AdminVendorsEnhanced from './Admin/AdminVendorsEnhanced';
import VendorDetailPage from './Admin/VendorDetailPage';
import PurchaseOrderDetailPage from './Admin/PurchaseOrderDetailPage';
import BillDetailPage from './Admin/BillDetailPage';
import AdminPayments from './Admin/AdminPayments';
import PaymentDetailPage from './Admin/PaymentDetailPage';
// P1 Invoices (AR Cockpit)
import AdminInvoices from './Admin/AdminInvoices';
// Catalog Imports module
import AdminCatalogImports from './Admin/AdminCatalogImports';
import AdminCatalogOverview from './Admin/AdminCatalogOverview';
import CatalogOverviewV2 from './Admin/CatalogOverviewV2';
import ImportDetailPage from './Admin/ImportDetailPage';
import CreateImportWizard from './Admin/CreateImportWizard';
import AdminVendorSources from './Admin/AdminVendorSources';
import AdminMappingProfiles from './Admin/AdminMappingProfiles';
import AdminLogoSettings from './Admin/AdminLogoSettings';
import VendorSyncHub from './Admin/VendorSyncHub';
import AdminInvoiceSettings from './Admin/AdminInvoiceSettings';
// Product Detail Modal
import ProductDetailModal from '../components/admin/ProductDetailModal';
// P1 Sales Tax & Settings
import AdminSalesTax from './Admin/AdminSalesTax';
// P1 Sales Tax Reports (CA-only CDTFA compliance)
import AdminSalesTaxReports from './Admin/AdminSalesTaxReports';
import SalesTaxReportDetail from './Admin/SalesTaxReportDetail';
// P2 Reminder Profiles
import AdminReminderProfiles from './Admin/AdminReminderProfiles';
// P3 Reminder Settings (Automated Reminders)
import AdminReminderSettings from './Admin/AdminReminderSettings';
// P1 Email Templates
import AdminEmailTemplates from './Admin/AdminEmailTemplates';
// P1 Security & User Management
import AdminSecuritySettings from './Admin/AdminSecuritySettings';
// Check Printing
import AdminBankAccounts from './Admin/AdminBankAccounts';
import AdminChecks from './Admin/AdminChecks';
import AdminCompanySettings from './Admin/AdminCompanySettings';
// Manual Product Editor
import AdminProductEditor from './Admin/AdminProductEditor';
// Vendor Credentials
import AdminVendorCredentials from './Admin/AdminVendorCredentials';
// Customer Statements (Reports)
import CustomerStatements from './Admin/CustomerStatements';
import ARAgingReport from './Admin/ARAgingReport';
import SalesSummaryReport from './Admin/SalesSummaryReport';
import PaymentHistoryReport from './Admin/PaymentHistoryReport';
import EmailHistory from './Admin/EmailHistory';
// Create New unified components (P1.1)
import CreateNewMenu from '../components/admin/CreateNewMenu';
import { CreateNewProvider, useCreateNew, MODAL_KEYS } from '../components/admin/CreateNewContext';
import CreateNewController from '../components/admin/CreateNewController';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// ==============================================
// SIDEBAR COMPONENT WITH RBAC
// ==============================================
const AdminSidebar = ({ activeTab, setActiveTab, productsBreakdown, permissions }) => {
  // P1: Detect mobile viewport
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
  
  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);
  
  // P1: Sidebar collapse state - auto-collapsed on mobile
  const [isCollapsed, setIsCollapsed] = useState(() => {
    if (window.innerWidth < 768) return true; // Auto-collapse on mobile
    const stored = localStorage.getItem('admin_sidebar_collapsed');
    return stored === 'true';
  });
  
  // P1: Mobile sidebar open state (overlay)
  const [mobileOpen, setMobileOpen] = useState(false);
  
  // P1: Sidebar section open/close state from localStorage
  const [openSections, setOpenSections] = useState(() => {
    const stored = localStorage.getItem('admin_sidebar_sections');
    if (stored) {
      try {
        return JSON.parse(stored);
      } catch (e) {
        // Invalid JSON, return defaults
      }
    }
    return {
      orders: true,
      expenses: true,
      master: true,
      catalog: true,
      settings: true,
      // Subsections for Settings
      'settings-rules': true,
      'settings-users': true,
      'settings-general': true
    };
  });

  // P1: Toggle sidebar collapse
  const toggleCollapse = () => {
    setIsCollapsed(prev => {
      const newValue = !prev;
      localStorage.setItem('admin_sidebar_collapsed', newValue.toString());
      return newValue;
    });
  };

  const toggleSection = (section, event) => {
    setOpenSections(prev => {
      const newState = {
        ...prev,
        [section]: !prev[section]
      };
      // Persist to localStorage
      localStorage.setItem('admin_sidebar_sections', JSON.stringify(newState));
      return newState;
    });
    
    // On mobile, scroll the clicked section into view when expanding
    if (isMobile && event?.target) {
      setTimeout(() => {
        event.target.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }, 100);
    }
  };

  const totalProducts = (productsBreakdown?.small_format || 0) + 
                        (productsBreakdown?.large_format || 0) + 
                        (productsBreakdown?.swag || 0) + 
                        (productsBreakdown?.awards || 0);

  // Get section permissions (default to all visible for admin)
  const sections = permissions?.sections || {
    logic_settings: { visible: true, editable: true },
    master_data: { visible: true, editable: true },
    order_management: { visible: true, editable: true },
    crm: { visible: true, editable: true },
    expenses: { visible: true, editable: true },
    catalog_imports: { visible: true, editable: true },
    settings: { visible: true, editable: true },
  };

  // Build menu sections based on permissions
  const menuSections = [
    {
      id: 'overview',
      label: 'Overview',
      icon: LayoutDashboard,
      isTop: true,
      tab: 'overview',
      visible: true // Always visible for admin panel users
    },
    // Order Management - Most roles have some access
    {
      id: 'orders',
      label: 'Order Management',
      icon: ShoppingCart,
      visible: sections.order_management?.visible,
      readOnly: !sections.order_management?.editable,
      items: [
        { id: 'orders-list', label: 'Orders', icon: ClipboardList, tab: 'orders' },
        { id: 'invoices', label: 'Invoices', icon: FileText, tab: 'invoices' },
        { id: 'customers', label: 'Customers', icon: Factory, tab: 'customers' },
        { id: 'crm-contacts', label: 'Contacts', icon: Users, tab: 'crm-contacts' },
        { id: 'tasks', label: 'Tasks', icon: ClipboardList, tab: 'tasks' },
        { id: 'activities', label: 'Activities', icon: Activity, tab: 'activities' },
        { id: 'leads', label: 'Leads Pipeline', icon: TrendingUp, tab: 'leads' },
        { id: 'contacts', label: 'Web Submission', icon: MessageSquare, tab: 'contacts' },
        { id: 'work-orders', label: 'Work Orders', icon: Truck, tab: 'work-orders' },
      ]
    },
    // Expenses - Admin/Accounting: full, Others: hidden or read-only
    {
      id: 'expenses',
      label: 'Expenses',
      icon: DollarSign,
      visible: sections.expenses?.visible,
      readOnly: !sections.expenses?.editable,
      items: [
        { id: 'vendors', label: 'Vendors', icon: Factory, tab: 'vendors' },
        { id: 'purchase-orders', label: 'Purchase Orders', icon: FileText, tab: 'purchase-orders' },
        { id: 'bills', label: 'Bills to Pay', icon: ClipboardList, tab: 'bills' },
        { id: 'expense-payments', label: 'Payments', icon: DollarSign, tab: 'expense-payments' },
        { id: 'checks', label: 'Checks', icon: FileText, tab: 'checks' },
      ]
    },
    // Catalog - V2 Template-Based Architecture
    {
      id: 'catalog',
      label: 'Catalog',
      icon: Package,
      visible: sections.master_data?.visible,
      readOnly: !sections.master_data?.editable,
      items: [
        { id: 'catalog-v2', label: 'Product Catalog', icon: LayoutTemplate, tab: 'catalog-v2' },
      ]
    },
    // Reports
    {
      id: 'reports',
      label: 'Reports',
      icon: BarChart3,
      visible: true,
      items: [
        { id: 'customer-statements', label: 'Customer Statements', icon: FileText, tab: 'customer-statements' },
        { id: 'ar-aging', label: 'AR Aging', icon: BarChart3, tab: 'ar-aging' },
        { id: 'sales-summary', label: 'Sales Summary', icon: TrendingUp, tab: 'sales-summary' },
        { id: 'payment-history', label: 'Payment History', icon: DollarSign, tab: 'payment-history' },
      ]
    },
    // Email History
    {
      id: 'email-history-section',
      label: 'Email History',
      icon: Mail,
      visible: true,
      items: [
        { id: 'email-history', label: 'Email History', icon: Mail, tab: 'email-history' },
      ]
    },
    // Settings - Admin only (merged Logic Settings + Settings with subsections)
    {
      id: 'settings',
      label: 'Settings',
      icon: Settings,
      visible: sections.settings?.visible,  // Settings section is admin-only
      readOnly: !sections.settings?.editable,
      subsections: [
        {
          id: 'settings-rules',
          label: 'Rules',
          icon: Calculator,
          items: [
            { id: 'pricing', label: 'Pricing', fullLabel: 'Pricing Rules', icon: Calculator, tab: 'pricing' },
            { id: 'promotions', label: 'Discounts', fullLabel: 'Discount / Promo', icon: Percent, tab: 'promotions' },
            { id: 'tax-zones', label: 'Sales Tax', icon: Percent, tab: 'tax-zones' },
            { id: 'tax-reports', label: 'Tax Reports', fullLabel: 'Sales Tax Reports', icon: FileText, tab: 'tax-reports' },
            { id: 'reminder-profiles', label: 'Profiles', fullLabel: 'Reminder Profiles', icon: Bell, tab: 'reminder-profiles' },
            { id: 'reminder-settings', label: 'Auto Reminders', fullLabel: 'Automated Reminders', icon: Clock, tab: 'reminder-settings' },
          ]
        },
        {
          id: 'settings-security',
          label: 'Security',
          icon: Shield,
          items: [
            { id: 'security', label: 'Security', fullLabel: 'Security & Users', icon: Shield, tab: 'security' },
            { id: 'settings-users', label: 'Users', fullLabel: 'User Management', icon: Users, tab: 'settings-users' },
            { id: 'role-management', label: 'Roles', fullLabel: 'Role Management', icon: Shield, tab: 'role-management' },
            { id: 'role-audit-log', label: 'Audit Log', fullLabel: 'Role Audit Log', icon: ClipboardList, tab: 'role-audit-log' },
          ]
        },
        {
          id: 'settings-general',
          label: 'General',
          icon: Settings,
          items: [
            { id: 'hero-slider', label: 'HERO Slider', icon: Megaphone, tab: 'hero-slider' },
            { id: 'logo-settings', label: 'Logo', fullLabel: 'Logo Settings', icon: ImageIcon, tab: 'logo-settings' },
            { id: 'invoice-settings', label: 'Invoice/Quote', fullLabel: 'Invoice Settings', icon: FileText, tab: 'invoice-settings' },
            { id: 'email-templates', label: 'Emails', fullLabel: 'Email Templates', icon: Mail, tab: 'email-templates' },
            { id: 'company-settings', label: 'Company Info', icon: Building, tab: 'company-settings' },
            { id: 'bank-accounts', label: 'Bank Accounts', icon: Building, tab: 'bank-accounts' },
            { id: 'vendor-credentials', label: 'Vendor Logins', fullLabel: 'Vendor Credentials', icon: Lock, tab: 'vendor-credentials' },
            { id: 'training-docs', label: 'Training Docs', fullLabel: 'Staff Training', icon: BookOpen, tab: 'training-docs' },
          ]
        }
      ]
    }
  ];

  // Filter to only visible sections
  const visibleSections = menuSections.filter(section => section.visible !== false);
  
  // Handle menu item click on mobile - close sidebar after selection
  const handleMobileMenuClick = (tab) => {
    setActiveTab(tab);
    if (isMobile) {
      setMobileOpen(false);
    }
  };

  // Mobile: Show hamburger button + slide-out overlay
  if (isMobile) {
    return (
      <>
        {/* Mobile hamburger button - fixed position */}
        <button
          onClick={() => setMobileOpen(true)}
          className="fixed top-4 left-4 z-50 p-2 bg-ivory-900 text-ivory-100 rounded-lg shadow-lg"
          data-testid="mobile-menu-btn"
        >
          <Menu className="w-6 h-6" />
        </button>
        
        {/* Mobile overlay backdrop */}
        {mobileOpen && (
          <div 
            className="fixed inset-0 bg-black/50 z-40"
            onClick={() => setMobileOpen(false)}
          />
        )}
        
        {/* Mobile slide-out sidebar */}
        <aside 
          className={`fixed inset-y-0 left-0 z-50 bg-ivory-900 text-ivory-100 w-72 transform transition-transform duration-300 ${
            mobileOpen ? 'translate-x-0' : '-translate-x-full'
          }`}
          data-testid="admin-sidebar-mobile"
        >
          {/* Mobile Header with close button */}
          <div className="p-4 border-b border-ivory-700 flex items-center justify-between">
            <Link to="/" className="flex items-center gap-3">
              <div className="w-10 h-10 bg-gradient-to-br from-gold-600 to-gold-500 rounded-lg flex items-center justify-center shadow-lg">
                <span className="text-white font-heading font-bold text-lg">E</span>
              </div>
              <div>
                <span className="text-lg font-heading font-semibold text-ivory-100">Engraveca</span>
                <span className="block text-xs text-gold-400 font-body">Admin Panel</span>
              </div>
            </Link>
            <button
              onClick={() => setMobileOpen(false)}
              className="p-2 rounded-lg text-ivory-400 hover:bg-ivory-800 hover:text-ivory-200"
              data-testid="mobile-menu-close"
            >
              <X className="w-5 h-5" />
            </button>
          </div>
          
          {/* Mobile Navigation - scrollable */}
          <nav className="p-3 pb-32 space-y-1 overflow-y-auto" style={{ maxHeight: 'calc(100vh - 80px)' }}>
            {visibleSections.map((section) => {
              if (section.isTop) {
                const Icon = section.icon;
                const isActive = activeTab === section.tab;
                return (
                  <button
                    key={section.id}
                    onClick={() => handleMobileMenuClick(section.tab)}
                    className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-lg font-body text-sm transition-all ${
                      isActive
                        ? 'bg-gold-500/20 text-gold-400 font-medium'
                        : 'text-ivory-300 hover:bg-ivory-800 hover:text-ivory-100'
                    }`}
                    data-testid={`admin-nav-${section.id}`}
                  >
                    <Icon className="w-5 h-5 flex-shrink-0" />
                    <span>{section.label}</span>
                  </button>
                );
              }
              
              // Section with items
              const Icon = section.icon;
              const isOpen = openSections[section.id];
              const sectionItems = section.items || section.subsections || [];
              const hasActiveItem = sectionItems.some(item => 
                item.tab === activeTab || item.items?.some(sub => sub.tab === activeTab)
              );
              
              return (
                <div key={section.id} className="space-y-1">
                  <button
                    onClick={(e) => toggleSection(section.id, e)}
                    className={`w-full flex items-center justify-between px-3 py-2.5 rounded-lg font-body text-sm transition-all ${
                      hasActiveItem
                        ? 'bg-ivory-800/50 text-gold-400'
                        : 'text-ivory-300 hover:bg-ivory-800 hover:text-ivory-100'
                    }`}
                    data-testid={`admin-nav-section-${section.id}`}
                  >
                    <div className="flex items-center gap-3">
                      <Icon className="w-5 h-5 flex-shrink-0" />
                      <span>{section.label}</span>
                    </div>
                    <ChevronDown className={`w-4 h-4 transition-transform ${isOpen ? 'rotate-180' : ''}`} />
                  </button>
                  
                  {isOpen && (section.items || section.subsections) && (
                    <div className="ml-4 space-y-1">
                      {(section.items || section.subsections).map((item) => {
                        if (item.items) {
                          // Subsection
                          const SubIcon = item.icon;
                          const isSubOpen = openSections[item.id];
                          const hasActiveSubItem = item.items?.some(sub => sub.tab === activeTab);
                          
                          return (
                            <div key={item.id} className="space-y-1">
                              <button
                                onClick={(e) => toggleSection(item.id, e)}
                                className={`w-full flex items-center justify-between px-3 py-2 rounded-lg font-body text-xs transition-all ${
                                  hasActiveSubItem
                                    ? 'bg-ivory-800/50 text-gold-400'
                                    : 'text-ivory-400 hover:bg-ivory-800 hover:text-ivory-200'
                                }`}
                                data-testid={`admin-nav-subsection-${item.id}`}
                              >
                                <div className="flex items-center gap-3">
                                  <SubIcon className="w-4 h-4 flex-shrink-0" />
                                  <span>{item.label}</span>
                                </div>
                                <ChevronDown className={`w-3 h-3 transition-transform ${isSubOpen ? 'rotate-180' : ''}`} />
                              </button>
                              
                              {isSubOpen && (
                                <div className="ml-4 space-y-1">
                                  {item.items.map((subItem) => {
                                    const SubItemIcon = subItem.icon;
                                    const isSubActive = activeTab === subItem.tab;
                                    return (
                                      <button
                                        key={subItem.id}
                                        onClick={() => handleMobileMenuClick(subItem.tab)}
                                        className={`w-full flex items-center gap-3 px-3 py-2 rounded-lg font-body text-xs transition-all ${
                                          isSubActive
                                            ? 'bg-gold-500/20 text-gold-400 font-medium'
                                            : 'text-ivory-400 hover:bg-ivory-800 hover:text-ivory-200'
                                        }`}
                                        data-testid={`admin-nav-${subItem.id}`}
                                      >
                                        <SubItemIcon className="w-4 h-4 flex-shrink-0" />
                                        <span>{subItem.label}</span>
                                      </button>
                                    );
                                  })}
                                </div>
                              )}
                            </div>
                          );
                        }
                        
                        // Regular item
                        const ItemIcon = item.icon;
                        const isActive = activeTab === item.tab;
                        return (
                          <button
                            key={item.id}
                            onClick={() => handleMobileMenuClick(item.tab)}
                            className={`w-full flex items-center gap-3 px-3 py-2 rounded-lg font-body text-xs transition-all ${
                              isActive
                                ? 'bg-gold-500/20 text-gold-400 font-medium'
                                : 'text-ivory-400 hover:bg-ivory-800 hover:text-ivory-200'
                            }`}
                            data-testid={`admin-nav-${item.id}`}
                          >
                            <ItemIcon className="w-4 h-4 flex-shrink-0" />
                            <span>{item.label}</span>
                          </button>
                        );
                      })}
                    </div>
                  )}
                </div>
              );
            })}
          </nav>
        </aside>
      </>
    );
  }

  // Desktop: Standard sidebar
  return (
    <aside 
      className={`bg-ivory-900 text-ivory-100 min-h-screen max-h-screen flex-shrink-0 sticky top-0 transition-all duration-300 flex flex-col ${
        isCollapsed ? 'w-16' : 'w-64'
      }`} 
      data-testid="admin-sidebar"
    >
      {/* Header */}
      <div className={`p-4 border-b border-ivory-700 ${isCollapsed ? 'px-2' : ''}`}>
        <div className="flex items-center justify-between">
          <Link to="/" className={`flex items-center gap-3 ${isCollapsed ? 'justify-center' : ''}`}>
            <div className="w-10 h-10 bg-gradient-to-br from-gold-600 to-gold-500 rounded-lg flex items-center justify-center shadow-lg flex-shrink-0">
              <span className="text-white font-heading font-bold text-lg">E</span>
            </div>
            {!isCollapsed && (
              <div>
                <span className="text-lg font-heading font-semibold text-ivory-100">Engraveca</span>
                <span className="block text-xs text-gold-400 font-body">Admin Panel</span>
              </div>
            )}
          </Link>
          <button
            onClick={toggleCollapse}
            className={`p-1.5 rounded-lg text-ivory-400 hover:bg-ivory-800 hover:text-ivory-200 transition-all ${
              isCollapsed ? 'ml-0' : ''
            }`}
            title={isCollapsed ? 'Expand sidebar' : 'Collapse sidebar'}
            data-testid="toggle-sidebar-btn"
          >
            {isCollapsed ? (
              <ChevronRight className="w-5 h-5" />
            ) : (
              <ChevronLeft className="w-5 h-5" />
            )}
          </button>
        </div>
      </div>

      {/* Navigation - scrollable */}
      <nav className={`p-3 space-y-1 flex-1 overflow-y-auto ${isCollapsed ? 'px-1' : ''}`}>
        {visibleSections.map((section) => {
          // Top-level item (no dropdown)
          if (section.isTop) {
            const Icon = section.icon;
            const isActive = activeTab === section.tab;
            return (
              <div key={section.id} className="relative group">
                <button
                  onClick={() => setActiveTab(section.tab)}
                  className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-lg font-body text-sm transition-all ${
                    isActive
                      ? 'bg-gold-500/20 text-gold-400 font-medium'
                      : 'text-ivory-300 hover:bg-ivory-800 hover:text-ivory-100'
                  } ${isCollapsed ? 'justify-center px-2' : ''}`}
                  data-testid={`admin-nav-${section.id}`}
                >
                  <Icon className="w-5 h-5 flex-shrink-0" />
                  {!isCollapsed && <span>{section.label}</span>}
                </button>
                {/* Tooltip for collapsed state - shows after 300ms delay */}
                {isCollapsed && (
                  <div 
                    className="absolute left-full top-1/2 -translate-y-1/2 ml-2 opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200 delay-300 z-50 pointer-events-none"
                    role="tooltip"
                  >
                    <div className="bg-ivory-800 text-ivory-100 px-3 py-1.5 rounded-lg shadow-lg whitespace-nowrap text-sm border border-ivory-700">
                      {section.label}
                    </div>
                  </div>
                )}
              </div>
            );
          }

          // Dropdown section
          const SectionIcon = section.icon;
          const isOpen = openSections[section.id];
          // Check for active item in regular items or subsections
          const hasActiveItem = section.items?.some(item => activeTab === item.tab) ||
            section.subsections?.some(sub => sub.items?.some(item => activeTab === item.tab));

          // In collapsed state, show only the icon with tooltip
          if (isCollapsed) {
            return (
              <div key={section.id} className="relative group">
                <button
                  onClick={() => toggleSection(section.id)}
                  className={`w-full flex items-center justify-center px-2 py-2.5 rounded-lg font-body text-sm transition-all ${
                    hasActiveItem
                      ? 'bg-ivory-800 text-ivory-100'
                      : 'text-ivory-400 hover:bg-ivory-800 hover:text-ivory-200'
                  }`}
                  data-testid={`admin-nav-section-${section.id}`}
                >
                  <SectionIcon className="w-5 h-5 flex-shrink-0" />
                </button>
                {/* Tooltip for collapsed state - shows after 300ms delay */}
                <div 
                  className="absolute left-full top-1/2 -translate-y-1/2 ml-2 opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200 delay-300 z-50 pointer-events-none"
                  role="tooltip"
                >
                  <div className="bg-ivory-800 text-ivory-100 px-3 py-1.5 rounded-lg shadow-lg whitespace-nowrap text-sm border border-ivory-700">
                    {section.label}
                  </div>
                </div>
              </div>
            );
          }

          return (
            <div key={section.id} className="space-y-1">
              <button
                onClick={() => toggleSection(section.id)}
                className={`w-full flex items-center justify-between px-3 py-2.5 rounded-lg font-body text-sm transition-all ${
                  hasActiveItem
                    ? 'bg-ivory-800 text-ivory-100'
                    : 'text-ivory-400 hover:bg-ivory-800 hover:text-ivory-200'
                }`}
                data-testid={`admin-nav-section-${section.id}`}
              >
                <div className="flex items-center gap-3">
                  <SectionIcon className="w-5 h-5 flex-shrink-0" />
                  <span className="font-semibold">{section.label}</span>
                  {section.readOnly && (
                    <Lock className="w-3 h-3 text-ivory-500" title="Read-only access" />
                  )}
                </div>
                {isOpen ? (
                  <ChevronDown className="w-4 h-4" />
                ) : (
                  <ChevronRight className="w-4 h-4" />
                )}
              </button>

              {isOpen && section.items && (
                <div className="ml-4 pl-4 border-l border-ivory-700 space-y-1">
                  {section.items.map((item) => {
                    const ItemIcon = item.icon;
                    const isActive = activeTab === item.tab;
                    return (
                      <button
                        key={item.id}
                        onClick={(e) => {
                          e.stopPropagation();
                          setActiveTab(item.tab);
                        }}
                        className={`w-full flex items-center justify-between px-3 py-2 rounded-lg font-body text-sm transition-all ${
                          isActive
                            ? 'bg-gold-500/20 text-gold-400 font-medium'
                            : 'text-ivory-400 hover:bg-ivory-800 hover:text-ivory-200'
                        }`}
                        data-testid={`admin-nav-${item.id}`}
                      >
                        <div className="flex items-center gap-3">
                          <ItemIcon className="w-4 h-4 flex-shrink-0" />
                          <span>{item.label}</span>
                        </div>
                        {item.badge > 0 && (
                          <span className="text-xs bg-ivory-700 text-ivory-300 px-2 py-0.5 rounded-full">
                            {item.badge.toLocaleString()}
                          </span>
                        )}
                      </button>
                    );
                  })}
                </div>
              )}

              {/* Subsections (for Settings with nested groups) */}
              {isOpen && section.subsections && (
                <div className="ml-3 pl-3 border-l border-ivory-700 space-y-1">
                  {section.subsections.map((subsection) => {
                    const SubIcon = subsection.icon;
                    const isSubOpen = openSections[subsection.id];
                    const hasActiveSubItem = subsection.items?.some(item => activeTab === item.tab);
                    
                    return (
                      <div key={subsection.id} className="space-y-0.5">
                        <button
                          onClick={(e) => toggleSection(subsection.id, e)}
                          className={`w-full flex items-center justify-between px-2 py-1.5 rounded-lg font-body text-xs transition-all ${
                            hasActiveSubItem
                              ? 'bg-ivory-800/50 text-ivory-100'
                              : 'text-ivory-400 hover:bg-ivory-800 hover:text-ivory-200'
                          }`}
                          data-testid={`admin-nav-subsection-${subsection.id}`}
                        >
                          <div className="flex items-center gap-2">
                            <SubIcon className="w-3.5 h-3.5 flex-shrink-0" />
                            <span className="font-medium whitespace-nowrap">{subsection.label}</span>
                          </div>
                          {isSubOpen ? (
                            <ChevronDown className="w-3 h-3" />
                          ) : (
                            <ChevronRight className="w-3 h-3" />
                          )}
                        </button>
                        
                        {isSubOpen && subsection.items && (
                          <div className="ml-2 pl-2 border-l border-ivory-700/50 space-y-0.5">
                            {subsection.items.map((item) => {
                              const ItemIcon = item.icon;
                              const isActive = activeTab === item.tab;
                              return (
                                <button
                                  key={item.id}
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    setActiveTab(item.tab);
                                  }}
                                  className={`w-full flex items-center gap-2 px-2 py-1.5 rounded-lg font-body text-xs transition-all ${
                                    isActive
                                      ? 'bg-gold-500/20 text-gold-400 font-medium'
                                      : 'text-ivory-400 hover:bg-ivory-800 hover:text-ivory-200'
                                  }`}
                                  data-testid={`admin-nav-${item.id}`}
                                  title={item.fullLabel || item.label}
                                >
                                  <ItemIcon className="w-3.5 h-3.5 flex-shrink-0" />
                                  <span className="truncate whitespace-nowrap">{item.label}</span>
                                </button>
                              );
                            })}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          );
        })}
      </nav>
    </aside>
  );
};

// ==============================================
// IMPERSONATION BANNER
// ==============================================
const ImpersonationBanner = () => {
  const [impersonationData, setImpersonationData] = useState(null);
  const [isEnding, setIsEnding] = useState(false);
  
  useEffect(() => {
    // Check if we're in an impersonation session
    const impersonation = localStorage.getItem('impersonation');
    if (impersonation) {
      try {
        setImpersonationData(JSON.parse(impersonation));
      } catch (e) {
        console.error('Failed to parse impersonation data:', e);
      }
    }
  }, []);
  
  const handleEndImpersonation = async () => {
    setIsEnding(true);
    try {
      const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/users/admin/end-impersonation`, {
        method: 'POST',
        credentials: 'include'
      });
      
      if (!res.ok) {
        const err = await res.json();
        throw new Error(err.detail || 'Failed to end impersonation');
      }
      
      // Clear impersonation data from localStorage
      localStorage.removeItem('impersonation');
      
      toast.success('Returned to admin session');
      
      // Reload to apply the restored admin session
      setTimeout(() => {
        window.location.href = '/admin';
      }, 500);
    } catch (err) {
      toast.error(err.message);
      setIsEnding(false);
    }
  };
  
  if (!impersonationData?.isImpersonation) return null;
  
  const targetUser = impersonationData.targetUser;
  const adminUser = impersonationData.impersonatedBy;
  
  return (
    <div 
      className="bg-amber-500 text-amber-950 px-4 py-2 flex items-center justify-between shadow-md z-50"
      data-testid="impersonation-banner"
    >
      <div className="flex items-center gap-3">
        <UserX className="w-5 h-5" />
        <span className="font-medium">
          Impersonation Mode: You are viewing as{' '}
          <strong>{targetUser?.name || targetUser?.email || 'Unknown User'}</strong>
        </span>
        <span className="text-amber-800 text-sm">
          (Admin: {adminUser?.email})
        </span>
      </div>
      <Button
        size="sm"
        variant="outline"
        className="bg-white hover:bg-amber-100 text-amber-900 border-amber-600"
        onClick={handleEndImpersonation}
        disabled={isEnding}
        data-testid="end-impersonation-btn"
      >
        {isEnding ? (
          <>
            <Loader2 className="w-4 h-4 mr-2 animate-spin" />
            Returning...
          </>
        ) : (
          <>
            <ArrowLeftCircle className="w-4 h-4 mr-2" />
            Return to Admin
          </>
        )}
      </Button>
    </div>
  );
};

// ==============================================
// MAIN ADMIN DASHBOARD CONTENT (uses context)
// ==============================================
const AdminDashboardContent = () => {
  const navigate = useNavigate();
  const location = useLocation();
  // Use stable search params to prevent infinite loops on mobile
  const { initialParams, setParam, setParams, removeParams, hasParam } = useStableSearchParams();
  const { user, logout, isAdmin, loading: authLoading, checkAuth } = useAuth();
  const { openCreateModal } = useCreateNew();
  const [dashboardData, setDashboardData] = useState(null);
  const [permissions, setPermissions] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  
  // Parse tab from URL path (e.g., /admin/orders -> 'orders')
  // Priority: URL path > search param > default 'overview'
  const getInitialTab = () => {
    // Check URL path first (e.g., /admin/orders, /admin/catalog-overview)
    const pathMatch = location.pathname.match(/^\/admin\/(.+)$/);
    if (pathMatch) {
      const pathTab = pathMatch[1];
      // Handle nested paths like /admin/catalog/overview -> catalog-overview
      return pathTab.replace('/', '-');
    }
    // Fallback to search param
    return initialParams.tab || 'overview';
  };
  
  // Initialize activeTab from URL path or search param (only on mount)
  const [activeTab, setActiveTab] = useState(getInitialTab);
  const [accessDenied, setAccessDenied] = useState(false);
  
  // URL filter state for "View All" functionality - initialized from URL on mount
  const [urlFilterAccountId, setUrlFilterAccountId] = useState(() => initialParams.account_id || null);
  const [urlFilterAccountName, setUrlFilterAccountName] = useState(() => 
    initialParams.account_name ? decodeURIComponent(initialParams.account_name) : null
  );
  const [urlFilterLeadId, setUrlFilterLeadId] = useState(() => initialParams.lead_id || null);
  const [urlFilterLeadName, setUrlFilterLeadName] = useState(() => 
    initialParams.lead_name ? decodeURIComponent(initialParams.lead_name) : null
  );
  const [urlFilterContactId, setUrlFilterContactId] = useState(() => initialParams.contact_id || null);
  const [urlFilterContactName, setUrlFilterContactName] = useState(() => 
    initialParams.contact_name ? decodeURIComponent(initialParams.contact_name) : null
  );
  const [urlFilterOrderId, setUrlFilterOrderId] = useState(() => initialParams.order_id || null);
  const [urlFilterOrderName, setUrlFilterOrderName] = useState(() => 
    initialParams.order_name ? decodeURIComponent(initialParams.order_name) : null
  );
  
  // Detail view states
  const [viewingCustomerId, setViewingCustomerId] = useState(null);
  const [viewingContactId, setViewingContactId] = useState(null);
  const [viewingLeadId, setViewingLeadId] = useState(null);
  const [viewingTaskId, setViewingTaskId] = useState(null);
  const [viewingOrderId, setViewingOrderId] = useState(null);
  // viewingQuoteId REMOVED - quotes are now on orders
  const [viewingWorkOrderId, setViewingWorkOrderId] = useState(null);
  const [viewingProofData, setViewingProofData] = useState(null); // { orderId, proofId }
  // viewingInvoiceId REMOVED - invoices are now on orders
  const [currentImportId, setCurrentImportId] = useState(null);
  
  // Expenses module detail view states
  const [viewingVendorId, setViewingVendorId] = useState(null);
  const [viewingPOId, setViewingPOId] = useState(null);
  const [viewingBillId, setViewingBillId] = useState(null);
  const [viewingPaymentId, setViewingPaymentId] = useState(null);
  
  // P1: Tax Reports detail view state
  const [viewingTaxReportId, setViewingTaxReportId] = useState(null);
  
  // Product detail modal state
  const [viewingProductId, setViewingProductId] = useState(null);
  
  // Product editor state (for create/edit flow)
  const [editingProductId, setEditingProductId] = useState(null);
  
  // Accounts list for Contact modal
  const [accounts, setAccounts] = useState([]);
  
  // Note: Create modal states moved to CreateNewContext (P1.1)
  
  // Track if first render is complete (for URL sync)
  const isFirstRender = useRef(true);
  
  // Sync tab to URL when it changes (skip first render)
  // Update both the URL path and search param for backward compatibility
  useEffect(() => {
    if (isFirstRender.current) {
      isFirstRender.current = false;
      // On first render, if we have a path-based tab, redirect to clean URL with search param
      const pathMatch = location.pathname.match(/^\/admin\/(.+)$/);
      if (pathMatch) {
        // Replace path-based URL with search param URL for consistency
        navigate(`/admin?tab=${activeTab}`, { replace: true });
      }
      return;
    }
    // Update URL with new tab
    navigate(`/admin?tab=${activeTab}`, { replace: true });
  }, [activeTab, navigate, location.pathname]);

  // Clear URL filter params when tab changes manually (not from URL)
  const handleSetActiveTab = useCallback((newTab, preserveContext = false) => {
    setActiveTab(newTab);
    // Clear all filter states (unless preserving context)
    if (!preserveContext) {
      setUrlFilterAccountId(null);
      setUrlFilterAccountName(null);
      setUrlFilterLeadId(null);
      setUrlFilterLeadName(null);
      setUrlFilterContactId(null);
      setUrlFilterContactName(null);
      setUrlFilterOrderId(null);
      setUrlFilterOrderName(null);
      // Clear URL filter params
      removeParams(['account_id', 'account_name', 'lead_id', 'lead_name', 
                    'contact_id', 'contact_name', 'order_id', 'order_name']);
    }
  }, [removeParams]);

  // Get active parent filter info for breadcrumb
  const getActiveParentFilter = useCallback(() => {
    if (urlFilterAccountId && urlFilterAccountName) {
      return { type: 'account', id: urlFilterAccountId, name: urlFilterAccountName };
    }
    if (urlFilterLeadId && urlFilterLeadName) {
      return { type: 'lead', id: urlFilterLeadId, name: urlFilterLeadName };
    }
    if (urlFilterContactId && urlFilterContactName) {
      return { type: 'contact', id: urlFilterContactId, name: urlFilterContactName };
    }
    if (urlFilterOrderId && urlFilterOrderName) {
      return { type: 'order', id: urlFilterOrderId, name: urlFilterOrderName };
    }
    return null;
  }, [urlFilterAccountId, urlFilterAccountName, urlFilterLeadId, urlFilterLeadName, 
      urlFilterContactId, urlFilterContactName, urlFilterOrderId, urlFilterOrderName]);

  // Navigate back to parent record
  const handleBackToParent = useCallback(() => {
    const parent = getActiveParentFilter();
    if (!parent) return;
    
    // Clear all filter states
    setUrlFilterAccountId(null);
    setUrlFilterAccountName(null);
    setUrlFilterLeadId(null);
    setUrlFilterLeadName(null);
    setUrlFilterContactId(null);
    setUrlFilterContactName(null);
    setUrlFilterOrderId(null);
    setUrlFilterOrderName(null);
    
    // Clear filter params from URL
    removeParams(['account_id', 'account_name', 'lead_id', 'lead_name',
                  'contact_id', 'contact_name', 'order_id', 'order_name']);
    
    // Navigate to parent detail page (tab sync happens via useEffect)
    switch (parent.type) {
      case 'account':
        setViewingCustomerId(parent.id);
        setActiveTab('customers');
        break;
      case 'lead':
        setViewingLeadId(parent.id);
        setActiveTab('leads');
        break;
      case 'contact':
        setViewingContactId(parent.id);
        setActiveTab('crm-contacts');
        break;
      case 'order':
        setViewingOrderId(parent.id);
        setActiveTab('orders');
        break;
      default:
        break;
    }
  }, [getActiveParentFilter, removeParams]);

  // Fetch permissions on mount
  useEffect(() => {
    const fetchPermissions = async () => {
      try {
        const permData = await authAPI.getPermissions();
        setPermissions(permData.permissions);
        
        // Check if user can access admin panel
        if (!permData.permissions?.can_access_admin) {
          setAccessDenied(true);
        }
      } catch (err) {
        console.error('Failed to fetch permissions:', err);
        // If permissions fail, fallback to basic admin check
        if (!isAdmin) {
          setAccessDenied(true);
        }
      }
    };

    if (!authLoading && user) {
      fetchPermissions();
    }
    // P1 FIX: Redirect to login if auth failed (no user after loading completes)
    if (!authLoading && !user) {
      navigate('/login');
    }
  }, [authLoading, user, isAdmin, navigate]);

  useEffect(() => {
    // Redirect if access denied
    if (accessDenied) {
      navigate('/');
    }
  }, [accessDenied, navigate]);

  useEffect(() => {
    const fetchDashboardData = async () => {
      try {
        const data = await adminAPI.getDashboard();
        setDashboardData(data);
      } catch (err) {
        setError('Failed to load dashboard data');
        console.error(err);
      } finally {
        setLoading(false);
      }
    };

    // Fetch if user has permissions or is admin (legacy)
    if (permissions?.can_access_admin || isAdmin) {
      fetchDashboardData();
    } else if (permissions && !permissions.can_access_admin) {
      setLoading(false);
    }
  }, [permissions, isAdmin]);

  // Load accounts for Contact modal
  useEffect(() => {
    const loadAccounts = async () => {
      try {
        const data = await crmAPI.getAccounts({ page_size: 250 });
        setAccounts(data.accounts || data.items || []);
      } catch (err) {
        console.error('Failed to load accounts for contact modal:', err);
      }
    };
    if (permissions?.can_access_admin || isAdmin) {
      loadAccounts();
    }
  }, [permissions, isAdmin]);

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  // Show access denied screen for customers/users
  if (accessDenied) {
    return (
      <div className="min-h-screen bg-ivory-50 flex items-center justify-center">
        <div className="text-center">
          <Lock className="w-16 h-16 text-red-400 mx-auto mb-4" />
          <h2 className="text-xl font-heading font-semibold text-ivory-800 mb-2">Access Denied</h2>
          <p className="text-ivory-600 font-body mb-4">
            You do not have permission to access the admin panel.
          </p>
          <Button onClick={() => navigate('/')}>
            Return to Home
          </Button>
        </div>
      </div>
    );
  }

  if (authLoading || loading) {
    return (
      <div className="min-h-screen bg-ivory-50 flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="w-12 h-12 animate-spin text-gold-500 mx-auto mb-4" />
          <p className="text-ivory-600 font-body">Loading admin panel...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-ivory-50 flex items-center justify-center">
        <div className="text-center">
          <p className="text-red-600 font-body mb-4">{error}</p>
          <Button onClick={() => window.location.reload()}>
            Try Again
          </Button>
        </div>
      </div>
    );
  }

  const productsBreakdown = dashboardData?.products_breakdown || {};
  const canSeeCost = permissions?.can_see_cost ?? true; // Default to true for admin
  const primaryRole = permissions?.primary_role || user?.role || 'admin';

  return (
    <div className="min-h-screen bg-ivory-50 flex flex-col" data-testid="admin-dashboard">
      {/* Impersonation Banner - Shows when admin is logged in as another user */}
      <ImpersonationBanner />
      
      <div className="flex flex-1">
        {/* Sidebar */}
        <AdminSidebar 
          activeTab={activeTab} 
          setActiveTab={handleSetActiveTab}
          productsBreakdown={productsBreakdown}
          permissions={permissions}
        />

      {/* Main Content */}
      <div className="flex-1 flex flex-col min-h-screen">
        {/* Top Header */}
        <header className="bg-white border-b border-ivory-200 sticky top-0 z-30">
          <div className="px-6 py-4 flex items-center justify-between">
            {/* Left side: Create New + Page Title */}
            <div className="flex items-center gap-6">
              {/* Create New Menu (P1.1 - uses context) */}
              <CreateNewMenu permissions={permissions} />
              
              {/* Page Title with optional Breadcrumb for filtered views */}
              <div className="flex items-center gap-2">
                {/* Breadcrumb: Show when viewing filtered list from parent record */}
                {(() => {
                  const parent = getActiveParentFilter();
                  if (!parent) return null;
                  
                  // Clean breadcrumb format: Orders > Order ORD-XXX > Account Name
                  // When viewing customer detail from order context
                  if (parent.type === 'order' && viewingCustomerId) {
                    return (
                      <>
                        <button 
                          onClick={() => {
                            // Go to orders list
                            setUrlFilterOrderId(null);
                            setUrlFilterOrderName(null);
                            setViewingCustomerId(null);
                            handleSetActiveTab('orders');
                          }}
                          className="text-copper-600 hover:text-copper-700 transition-colors text-sm"
                          data-testid="breadcrumb-orders"
                        >
                          Orders
                        </button>
                        <ChevronRight className="w-4 h-4 text-ivory-400" />
                        <button 
                          onClick={handleBackToParent}
                          className="text-copper-600 hover:text-copper-700 transition-colors text-sm"
                          data-testid="breadcrumb-back-to-parent"
                        >
                          {parent.name}
                        </button>
                        <ChevronRight className="w-4 h-4 text-ivory-400" />
                      </>
                    );
                  }
                  
                  // Default breadcrumb format for other parent types
                  const parentLabels = {
                    account: 'Customer',
                    lead: 'Lead',
                    contact: 'Contact',
                    order: 'Order'
                  };
                  
                  return (
                    <>
                      <button 
                        onClick={handleBackToParent}
                        className="flex items-center gap-1 text-copper-600 hover:text-copper-700 transition-colors"
                        data-testid="breadcrumb-back-to-parent"
                      >
                        <ChevronLeft className="w-4 h-4" />
                        <span className="text-xs text-copper-500">{parentLabels[parent.type]}:</span>
                        <span className="font-medium text-sm">{parent.name}</span>
                      </button>
                      <ChevronRight className="w-4 h-4 text-ivory-400" />
                    </>
                  );
                })()}
                <h1 className="text-xl font-heading font-bold text-ivory-800 capitalize">
                  {activeTab === 'overview' ? 'Dashboard Overview' : 
                   activeTab === 'hero-slider' ? 'HERO Slider' :
                   activeTab === 'product-types' ? 'Product Types' :
                   activeTab === 'catalog-overview' ? 'Catalog Overview' :
                   activeTab === 'catalog-imports' ? 'Catalog Imports' :
                   activeTab === 'import-detail' ? 'Import Details' :
                   activeTab === 'create-import' ? 'New Import' :
                   activeTab === 'vendor-import' ? 'Vendor Import' :
                   activeTab === 'vendor-sources' ? 'Vendor Sources' :
                   activeTab === 'mapping-profiles' ? 'Mapping Profiles' :
                   activeTab === 'tax-zones' ? 'Sales Tax' :
                   activeTab === 'tax-reports' ? 'Tax Reports' :
                   activeTab === 'customer-statements' ? 'Customer Statements' :
                   activeTab === 'ar-aging' ? 'AR Aging Report' :
                   activeTab === 'sales-summary' ? 'Sales Summary' :
                   activeTab === 'payment-history' ? 'Payment History' :
                   activeTab === 'email-history' ? 'Email History' :
                   activeTab.replace('-', ' ')}
                </h1>
              </div>
            </div>
            
            <div className="flex items-center gap-4">
              <div className="text-right">
                <span className="text-ivory-600 font-body text-sm block">
                  {user?.email}
                </span>
                <span className="text-xs text-ivory-400 capitalize">
                  {primaryRole.replace('_', ' ')}
                </span>
              </div>
              <Button
                onClick={handleLogout}
                variant="outline"
                size="sm"
                className="border-ivory-300 hover:bg-ivory-200/50"
              >
                <LogOut className="w-4 h-4 mr-2" />
                Logout
              </Button>
            </div>
          </div>
        </header>

        {/* Content Area */}
        <main className={`flex-1 overflow-auto ${activeTab === 'invoice-settings' ? 'overflow-hidden' : 'p-6'}`}>
          {/* Invoice Settings Tab - Full height, no padding */}
          {activeTab === 'invoice-settings' && (
            <div className="h-[calc(100vh-64px)]">
              <AdminInvoiceSettings />
            </div>
          )}
          {activeTab === 'overview' && (
            <AdminOverview dashboardData={dashboardData} productsBreakdown={productsBreakdown} />
          )}

          {/* Products Tab - Using Enhanced Component */}
          {activeTab === 'products' && (
            <AdminProductsEnhanced 
              onViewProduct={(productId) => setViewingProductId(productId)}
              onEditProduct={(productId) => {
                setEditingProductId(productId);
                setActiveTab('product-editor');
              }}
              onCreateProduct={() => {
                setEditingProductId(null);
                setActiveTab('product-editor');
              }}
            />
          )}

          {/* Orders Tab - Using Enhanced Component */}
          {activeTab === 'orders' && (
            viewingOrderId ? (
              <OrderDetailPage
                orderId={viewingOrderId}
                onBack={() => setViewingOrderId(null)}
                onNavigateToCustomer={(customerId, sourceOrderId) => {
                  // Store source order for breadcrumb navigation
                  if (sourceOrderId) {
                    setUrlFilterOrderId(sourceOrderId);
                    setUrlFilterOrderName(`Order ${sourceOrderId}`);
                  }
                  setViewingCustomerId(customerId);
                  // Use preserveContext to keep the order breadcrumb
                  handleSetActiveTab('customers', !!sourceOrderId);
                }}
                onNavigateToContact={(contactId) => {
                  setViewingContactId(contactId);
                  handleSetActiveTab('crm-contacts');
                }}
                // onNavigateToQuote REMOVED - quotes now unified on orders
                onNavigateToWorkOrder={(workOrderId) => {
                  setViewingWorkOrderId(workOrderId);
                  handleSetActiveTab('work-orders');
                }}
                onNavigateToProof={(orderId, proofId) => {
                  setViewingProofData({ orderId, proofId });
                }}
                onNavigateToTask={(taskId) => {
                  setViewingTaskId(taskId);
                  handleSetActiveTab('tasks');
                }}
                // onNavigateToInvoice REMOVED - invoices now unified on orders
              />
            ) : (
              <AdminOrdersEnhanced 
                onViewOrder={(orderId) => setViewingOrderId(orderId)}
                onCreateOrder={() => openCreateModal(MODAL_KEYS.ORDER)}
                onCombineOrders={(orderIds) => {
                  openCreateModal(MODAL_KEYS.ORDER, {
                    prefillFromOrderIds: orderIds,
                  });
                }}
                initialAccountId={urlFilterAccountId}
                initialContactId={urlFilterContactId}
                initialLeadId={urlFilterLeadId}
              />
            )
          )}

          {/* Invoices Tab (AR Cockpit) */}
          {activeTab === 'invoices' && (
            <AdminInvoices 
              onViewOrder={(orderId) => {
                setViewingOrderId(orderId);
                handleSetActiveTab('orders');
              }}
              onRecordPayment={(invoiceData) => {
                // Navigate to payments with invoice pre-selected
                // For now, just navigate to payments - the AdminInvoices component
                // has its own CreatePaymentModal with prefilled data
              }}
            />
          )}

          {/* Contacts Tab */}
          {activeTab === 'contacts' && (
            <AdminContacts />
          )}

          {/* Images Tab */}
          {activeTab === 'images' && (
            <AdminProductImages />
          )}

          {/* Pricing Tab */}
          {activeTab === 'pricing' && (
            <AdminPricing />
          )}

          {/* Manufacturers Tab - Using Enhanced Component */}
          {activeTab === 'manufacturers' && (
            <AdminManufacturersEnhanced />
          )}

          {/* Promotions Tab */}
          {activeTab === 'promotions' && (
            <AdminPromotions />
          )}

          {/* Hero Slider Tab */}
          {activeTab === 'hero-slider' && (
            <AdminHeroSlider />
          )}

          {/* Security Tab (replaces Users) */}
          {activeTab === 'security' && (
            <AdminSecuritySettings />
          )}
          
          {/* Legacy Users Tab - redirect to security */}
          {activeTab === 'users' && (
            <AdminSecuritySettings />
          )}

          {/* Users List with Pay Later Toggle */}
          {activeTab === 'settings-users' && (
            <AdminUsers />
          )}

          {/* Categories Tab */}
          {activeTab === 'categories' && (
            <AdminCategories />
          )}

          {/* Product Types Tab */}
          {activeTab === 'product-types' && (
            <AdminProductTypes />
          )}

          {/* CRM Tabs */}
          {activeTab === 'leads' && (
            viewingLeadId ? (
              <LeadDetailPage
                leadId={viewingLeadId}
                onBack={() => setViewingLeadId(null)}
                onNavigateToCustomer={(customerId) => {
                  setViewingCustomerId(customerId);
                  setActiveTab('customers');
                }}
                onNavigateToContact={(contactId) => {
                  setViewingContactId(contactId);
                  setActiveTab('crm-contacts');
                }}
                onNavigateToOrder={(orderId) => {
                  setViewingOrderId(orderId);
                  setActiveTab('orders');
                }}
                // onNavigateToQuote - quotes are now on orders, navigate to orders in quote mode
                onNavigateToQuote={(quoteIdOrObj) => {
                  setActiveTab('orders-quote-mode');
                }}
                onNavigateToTask={(taskId) => {
                  setViewingTaskId(taskId);
                  setActiveTab('tasks');
                }}
              />
            ) : (
              <AdminLeads 
                onViewLead={(leadId) => setViewingLeadId(leadId)}
                initialContactId={urlFilterContactId}
              />
            )
          )}

          {activeTab === 'tasks' && (
            viewingTaskId ? (
              <TaskDetailPage
                taskId={viewingTaskId}
                onBack={() => setViewingTaskId(null)}
                onNavigateToCustomer={(customerId) => {
                  setViewingCustomerId(customerId);
                  handleSetActiveTab('customers');
                }}
                onNavigateToLead={(leadId) => {
                  setViewingLeadId(leadId);
                  handleSetActiveTab('leads');
                }}
                // onNavigateToQuote - quotes are now on orders
                onNavigateToQuote={(quoteId) => {
                  handleSetActiveTab('orders-quote-mode');
                }}
                onNavigateToOrder={(orderId) => {
                  setViewingOrderId(orderId);
                  handleSetActiveTab('orders');
                }}
              />
            ) : (
              <AdminTasks 
                onViewTask={(taskId) => setViewingTaskId(taskId)}
                initialAccountId={urlFilterAccountId}
                initialLeadId={urlFilterLeadId}
                initialOrderId={urlFilterOrderId}
              />
            )
          )}

          {/* Activities Tab */}
          {activeTab === 'activities' && (
            <AdminActivities
              initialAccountId={urlFilterAccountId}
              initialLeadId={urlFilterLeadId}
              initialContactId={urlFilterContactId}
              initialOrderId={urlFilterOrderId}
            />
          )}

          {activeTab === 'customers' && (
            viewingCustomerId ? (
              <CustomerDetailPage
                customerId={viewingCustomerId}
                onBack={() => setViewingCustomerId(null)}
                onNavigateToContact={(contactId) => {
                  setViewingContactId(contactId);
                  setActiveTab('crm-contacts');
                }}
                onNavigateToOrder={(orderId) => {
                  // Navigate to existing order only
                  if (typeof orderId === 'string') {
                    setViewingOrderId(orderId);
                    setActiveTab('orders');
                  }
                }}
                // onNavigateToQuote - quotes are now on orders
                onNavigateToQuote={({ create, account_id }) => {
                  setActiveTab('orders-quote-mode');
                }}
                onNavigateToTask={(taskId) => {
                  setViewingTaskId(taskId);
                  setActiveTab('tasks');
                }}
                onCreateOrder={(customer) => {
                  openCreateModal(MODAL_KEYS.ORDER, {
                    accountId: customer.id,
                    customerName: customer.name,
                    email: customer.email,
                    phone: customer.phone,
                  });
                }}
                onReorderFromOrders={(orderIds) => {
                  openCreateModal(MODAL_KEYS.ORDER, {
                    prefillFromOrderIds: orderIds,
                  });
                }}
              />
            ) : (
              <AdminAccountsEnhanced 
                onViewCustomer={(customerId) => setViewingCustomerId(customerId)}
                onCreateOrder={(customer) => {
                  openCreateModal(MODAL_KEYS.ORDER, {
                    accountId: customer.id,
                    customerName: customer.name,
                    email: customer.email,
                    phone: customer.phone,
                  });
                }}
              />
            )
          )}

          {activeTab === 'crm-contacts' && (
            viewingContactId ? (
              <ContactDetailPage
                contactId={viewingContactId}
                onBack={() => setViewingContactId(null)}
                onNavigateToCustomer={(customerId) => {
                  setViewingCustomerId(customerId);
                  handleSetActiveTab('customers');
                }}
                onNavigateToLead={(leadIdOrObj) => {
                  if (typeof leadIdOrObj === 'string') {
                    setViewingLeadId(leadIdOrObj);
                  }
                  handleSetActiveTab('leads');
                }}
                onNavigateToOrder={(orderId) => {
                  setViewingOrderId(orderId);
                  handleSetActiveTab('orders');
                }}
                // onNavigateToQuote - quotes are now on orders
                onNavigateToQuote={(quoteIdOrObj) => {
                  handleSetActiveTab('orders-quote-mode');
                }}
              />
            ) : (
              <AdminContactsEnhanced 
                onViewContact={(contactId) => setViewingContactId(contactId)}
                initialAccountId={urlFilterAccountId}
              />
            )
          )}

          {/* Orders in Quote Mode Tab - Unified (replaces old Quotes) */}
          {activeTab === 'orders-quote-mode' && (
            viewingOrderId ? (
              <OrderDetailPage
                orderId={viewingOrderId}
                onBack={() => setViewingOrderId(null)}
                onNavigateToCustomer={(customerId) => {
                  setViewingCustomerId(customerId);
                  setActiveTab('customers');
                }}
                onNavigateToContact={(contactId) => {
                  setViewingContactId(contactId);
                  setActiveTab('crm-contacts');
                }}
                onNavigateToTask={(taskId) => {
                  setViewingTaskId(taskId);
                  setActiveTab('tasks');
                }}
              />
            ) : (
              <AdminOrdersEnhanced 
                filterMode="quote"
                onViewOrder={(orderId) => setViewingOrderId(orderId)}
                onCreateOrder={() => openCreateModal(MODAL_KEYS.ORDER)}
                onCombineOrders={(orderIds) => {
                  openCreateModal(MODAL_KEYS.ORDER, {
                    prefillFromOrderIds: orderIds,
                  });
                }}
              />
            )
          )}

          {/* Work Orders Tab */}
          {activeTab === 'work-orders' && (
            viewingWorkOrderId ? (
              <WorkOrderDetailPage
                workOrderId={viewingWorkOrderId}
                onBack={() => setViewingWorkOrderId(null)}
                onNavigateToOrder={(orderId) => {
                  setViewingWorkOrderId(null);
                  setViewingOrderId(orderId);
                  setActiveTab('orders');
                }}
              />
            ) : (
              <AdminWorkOrders 
                onViewWorkOrder={(workOrderId) => setViewingWorkOrderId(workOrderId)}
              />
            )
          )}

          {/* Orders with Invoice Issued Tab - Unified (replaces old Invoices) */}
          {activeTab === 'orders-invoiced' && (
            viewingOrderId ? (
              <OrderDetailPage
                orderId={viewingOrderId}
                onBack={() => setViewingOrderId(null)}
                onNavigateToCustomer={(customerId) => {
                  setViewingCustomerId(customerId);
                  setActiveTab('customers');
                }}
                onNavigateToContact={(contactId) => {
                  setViewingContactId(contactId);
                  setActiveTab('crm-contacts');
                }}
                onNavigateToTask={(taskId) => {
                  setViewingTaskId(taskId);
                  setActiveTab('tasks');
                }}
              />
            ) : (
              <AdminOrdersEnhanced 
                filterMode="invoiced"
                onViewOrder={(orderId) => setViewingOrderId(orderId)}
                onCreateOrder={() => openCreateModal(MODAL_KEYS.ORDER)}
                onCombineOrders={(orderIds) => {
                  openCreateModal(MODAL_KEYS.ORDER, {
                    prefillFromOrderIds: orderIds,
                  });
                }}
              />
            )
          )}

          {/* Role Management Tab */}
          {activeTab === 'role-management' && (
            <AdminRoleManagement 
              onNavigateToAuditLog={() => setActiveTab('role-audit-log')}
            />
          )}

          {/* Role Audit Log Tab */}
          {activeTab === 'role-audit-log' && (
            <AdminRoleAuditLog />
          )}

          {/* Sales Tax Tab (P1) */}
          {activeTab === 'tax-zones' && (
            <AdminSalesTax />
          )}

          {/* Sales Tax Reports Tab (P1 - CDTFA Compliance) */}
          {activeTab === 'tax-reports' && (
            viewingTaxReportId ? (
              <SalesTaxReportDetail 
                reportId={viewingTaxReportId}
                onBack={() => setViewingTaxReportId(null)}
                onNavigateToOrder={(orderId) => {
                  setViewingOrderId(orderId);
                  setActiveTab('orders');
                }}
              />
            ) : (
              <AdminSalesTaxReports 
                onViewReport={(reportId) => setViewingTaxReportId(reportId)}
              />
            )
          )}

          {/* Reminder Profiles Tab (P2) */}
          {activeTab === 'reminder-profiles' && (
            <AdminReminderProfiles />
          )}

          {/* Reminder Settings Tab (P3 - Automated Reminders) */}
          {activeTab === 'reminder-settings' && (
            <AdminReminderSettings />
          )}

          {/* Email Templates Tab (P1) */}
          {activeTab === 'email-templates' && (
            <AdminEmailTemplates />
          )}

          {/* Logo Settings Tab */}
          {activeTab === 'logo-settings' && (
            <AdminLogoSettings />
          )}

          {/* Expenses Module Tabs */}
          {activeTab === 'vendors' && (
            viewingVendorId ? (
              <VendorDetailPage
                vendorId={viewingVendorId}
                onBack={() => setViewingVendorId(null)}
                onNavigateToPO={(poId) => {
                  setViewingPOId(poId);
                  setActiveTab('purchase-orders');
                }}
                onNavigateToBill={(billId) => {
                  setViewingBillId(billId);
                  setActiveTab('bills');
                }}
                onCreatePO={(vendor) => {
                  openCreateModal(MODAL_KEYS.PURCHASE_ORDER, { vendorId: vendor.id, vendorName: vendor.name });
                }}
                onCreateBill={(vendor) => {
                  openCreateModal(MODAL_KEYS.BILL, { vendorId: vendor.id, vendorName: vendor.name });
                }}
              />
            ) : (
              <AdminVendorsEnhanced 
                onViewVendor={(vendorId) => setViewingVendorId(vendorId)}
              />
            )
          )}

          {/* Purchase Orders - Using Enhanced Component */}
          {activeTab === 'purchase-orders' && (
            viewingPOId ? (
              <PurchaseOrderDetailPage
                poId={viewingPOId}
                onBack={() => setViewingPOId(null)}
                onNavigateToVendor={(vendorId) => {
                  setViewingVendorId(vendorId);
                  setActiveTab('vendors');
                }}
                onNavigateToBill={(billId) => {
                  setViewingBillId(billId);
                  setActiveTab('bills');
                }}
                onCreateBill={(po, vendor) => {
                  openCreateModal(MODAL_KEYS.BILL, { 
                    vendorId: vendor?.id, 
                    vendorName: vendor?.name,
                    poId: po?.id,
                    poNumber: po?.po_number,
                  });
                }}
              />
            ) : (
              <AdminPurchaseOrdersEnhanced 
                onViewPO={(poId) => setViewingPOId(poId)}
                onCreatePO={() => {
                  openCreateModal(MODAL_KEYS.PURCHASE_ORDER);
                }}
              />
            )
          )}

          {activeTab === 'bills' && (
            viewingBillId ? (
              <BillDetailPage
                billId={viewingBillId}
                onBack={() => setViewingBillId(null)}
                onNavigateToVendor={(vendorId) => {
                  setViewingVendorId(vendorId);
                  setActiveTab('vendors');
                }}
                onNavigateToPO={(poId) => {
                  setViewingPOId(poId);
                  setActiveTab('purchase-orders');
                }}
                onNavigateToPayment={(paymentId) => {
                  setViewingPaymentId(paymentId);
                  setActiveTab('expense-payments');
                }}
                onRecordPayment={(billContext) => {
                  openCreateModal(MODAL_KEYS.PAYMENT, {
                    vendorId: billContext.vendorId,
                    vendorName: billContext.vendorName,
                    billId: billContext.billId,
                    billNumber: billContext.billNumber,
                    billAmountCents: billContext.billAmountCents,
                    billBalanceCents: billContext.billBalanceCents,
                  });
                }}
              />
            ) : (
              <AdminBillsEnhanced 
                onViewBill={(billId) => setViewingBillId(billId)}
                onCreateBill={() => {
                  openCreateModal(MODAL_KEYS.BILL);
                }}
              />
            )
          )}

          {activeTab === 'expense-payments' && (
            viewingPaymentId ? (
              <PaymentDetailPage
                paymentId={viewingPaymentId}
                onBack={() => setViewingPaymentId(null)}
                onNavigateToVendor={(vendorId) => {
                  setViewingVendorId(vendorId);
                  setActiveTab('vendors');
                }}
                onNavigateToBill={(billId) => {
                  setViewingBillId(billId);
                  setActiveTab('bills');
                }}
                onNavigateToInvoice={(invoiceId) => {
                  // Navigate to invoiced orders tab with specific order
                  // Invoices are tied to orders now
                  setActiveTab('orders-invoiced');
                }}
              />
            ) : (
              <AdminPayments 
                onViewPayment={(paymentId) => setViewingPaymentId(paymentId)}
              />
            )
          )}

          {/* Check Printing Module */}
          {activeTab === 'checks' && (
            <AdminChecks />
          )}

          {activeTab === 'bank-accounts' && (
            <AdminBankAccounts />
          )}

          {activeTab === 'company-settings' && (
            <AdminCompanySettings />
          )}

          {activeTab === 'vendor-credentials' && (
            <AdminVendorCredentials />
          )}

          {activeTab === 'training-docs' && (
            <DocsViewer />
          )}

          {/* Customer Statements (Reports) */}
          {activeTab === 'customer-statements' && (
            <CustomerStatements
              initialAccountId={urlFilterAccountId}
              onNavigateToCustomer={(customerId) => {
                setViewingCustomerId(customerId);
                handleSetActiveTab('customers');
              }}
            />
          )}

          {/* AR Aging Report */}
          {activeTab === 'ar-aging' && (
            <ARAgingReport />
          )}

          {/* Sales Summary Report */}
          {activeTab === 'sales-summary' && (
            <SalesSummaryReport />
          )}

          {/* Payment History Report */}
          {activeTab === 'payment-history' && (
            <PaymentHistoryReport />
          )}

          {/* Email History */}
          {activeTab === 'email-history' && (
            <EmailHistory />
          )}

          {/* Catalog Imports Module Tabs */}
          {activeTab === 'catalog-overview' && (
            <AdminCatalogOverview />
          )}

          {/* NEW: Catalog V2 - Template-based product management */}
          {activeTab === 'catalog-v2' && (
            <CatalogOverviewV2 />
          )}

          {/* Manual Product Editor */}
          {activeTab === 'product-editor' && (
            <AdminProductEditor
              productId={editingProductId}
              onBack={() => {
                setEditingProductId(null);
                setActiveTab('products');
              }}
              onSave={() => {
                setEditingProductId(null);
                setActiveTab('products');
              }}
            />
          )}

          {activeTab === 'catalog-imports' && (
            <AdminCatalogImports
              onViewImport={(importId) => {
                setActiveTab('import-detail');
                setCurrentImportId(importId);
              }}
              onCreateImport={() => setActiveTab('create-import')}
            />
          )}

          {activeTab === 'import-detail' && currentImportId && (
            <ImportDetailPage
              importId={currentImportId}
              onBack={() => setActiveTab('catalog-imports')}
              userRole={primaryRole}
            />
          )}

          {activeTab === 'create-import' && (
            <CreateImportWizard
              onBack={() => setActiveTab('catalog-imports')}
              onComplete={(importId) => {
                setCurrentImportId(importId);
                setActiveTab('import-detail');
              }}
            />
          )}

          {activeTab === 'vendor-sources' && (
            <AdminVendorSources />
          )}

          {activeTab === 'vendor-import' && (
            <VendorSyncHub />
          )}

          {activeTab === 'mapping-profiles' && (
            <AdminMappingProfiles />
          )}
        </main>
      </div>
      
      {/* Unified Create New Controller (P1.1) */}
      <CreateNewController
        onOrderSuccess={(orderId) => {
          setViewingOrderId(orderId);
          setActiveTab('orders');
        }}
        onCustomerSuccess={(customerId) => {
          setViewingCustomerId(customerId);
          setActiveTab('customers');
        }}
        onContactSuccess={(contact) => {
          // Navigate to contacts list or contact detail
          setActiveTab('crm-contacts');
        }}
        onVendorSuccess={(vendorId) => {
          setViewingVendorId(vendorId);
          setActiveTab('vendors');
        }}
        onProductSuccess={() => {
          setActiveTab('products');
        }}
        onPOSuccess={(poId) => {
          setViewingPOId(poId);
          setActiveTab('purchase-orders');
        }}
        onBillSuccess={(billId) => {
          setViewingBillId(billId);
          setActiveTab('bills');
        }}
        onPaymentSuccess={(paymentId) => {
          setViewingPaymentId(paymentId);
          setActiveTab('expense-payments');
        }}
        accounts={accounts}
      />

      {/* Product Detail Modal */}
      <ProductDetailModal
        open={!!viewingProductId}
        onOpenChange={(open) => !open && setViewingProductId(null)}
        productId={viewingProductId}
      />
      </div>
    </div>
  );
};

// ==============================================
// WRAPPER WITH PROVIDER
// ==============================================
const AdminDashboard = () => {
  return (
    <CreateNewProvider>
      <AdminDashboardContent />
    </CreateNewProvider>
  );
};

// ==============================================
// OVERVIEW COMPONENT
// ==============================================
const AdminOverview = ({ dashboardData, productsBreakdown }) => {
  // Map stats from backend response (stats.total_orders) to expected format
  const stats = dashboardData?.stats || {};
  
  const statCards = [
    {
      title: 'Total Orders',
      value: stats.total_orders || dashboardData?.orders?.total || 0,
      icon: ShoppingCart,
      color: 'text-blue-600',
      bgColor: 'bg-blue-50',
      subtext: `${stats.pending_orders || dashboardData?.orders?.pending || 0} pending`
    },
    {
      title: 'Revenue',
      value: `$${(stats.total_revenue || dashboardData?.orders?.revenue || 0).toLocaleString()}`,
      icon: DollarSign,
      color: 'text-green-600',
      bgColor: 'bg-green-50',
      subtext: 'Total sales'
    },
    {
      title: 'Products',
      value: stats.total_products || (productsBreakdown.small_format || 0) + 
             (productsBreakdown.large_format || 0) + 
             (productsBreakdown.swag || 0) + 
             (productsBreakdown.awards || 0),
      icon: Package,
      color: 'text-purple-600',
      bgColor: 'bg-purple-50',
      subtext: 'Total catalog items'
    },
    {
      title: 'Contacts',
      value: stats.total_contacts || dashboardData?.contacts?.total || 0,
      icon: MessageSquare,
      color: 'text-gold-600',
      bgColor: 'bg-gold-50',
      subtext: `${stats.new_contacts || dashboardData?.contacts?.unread || 0} unread`
    }
  ];

  return (
    <div className="space-y-8">
      {/* Stats Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
        {statCards.map((stat, index) => (
          <div
            key={index}
            className="bg-white rounded-xl border border-ivory-200 p-6 shadow-soft"
          >
            <div className="flex items-start justify-between mb-4">
              <div className={`w-12 h-12 rounded-xl ${stat.bgColor} flex items-center justify-center`}>
                <stat.icon className={`w-6 h-6 ${stat.color}`} />
              </div>
            </div>
            <h3 className="text-2xl font-heading font-semibold text-ivory-800 mb-1">
              {stat.value}
            </h3>
            <p className="text-ivory-500 font-body text-sm">{stat.title}</p>
            <p className="text-ivory-400 font-body text-xs mt-1">{stat.subtext}</p>
          </div>
        ))}
      </div>

      {/* Products Breakdown */}
      <div className="bg-white rounded-xl border border-ivory-200 p-6 shadow-soft">
        <h3 className="text-lg font-heading font-semibold text-ivory-800 mb-4">Products by Category</h3>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          {[
            { name: 'Small Format', count: productsBreakdown.small_format, color: 'bg-blue-500' },
            { name: 'Large Format', count: productsBreakdown.large_format, color: 'bg-green-500' },
            { name: 'Promo Swag', count: productsBreakdown.swag, color: 'bg-purple-500' },
            { name: 'Awards', count: productsBreakdown.awards, color: 'bg-gold-500' },
          ].map((cat, i) => (
            <div key={i} className="flex items-center gap-3 p-3 bg-ivory-50 rounded-lg">
              <div className={`w-3 h-3 rounded-full ${cat.color}`}></div>
              <div>
                <p className="text-ivory-800 font-body font-semibold text-lg">{(cat.count || 0).toLocaleString()}</p>
                <p className="text-ivory-500 font-body text-xs">{cat.name}</p>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

// ==============================================
// ORDER DETAILS INLINE WRAPPER
// ==============================================
const AdminOrderDetailsInline = ({ orderId, onBack }) => {
  const [order, setOrder] = useState(null);
  const [proofs, setProofs] = useState([]);
  const [workOrders, setWorkOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [updating, setUpdating] = useState(false);
  const [showStatusModal, setShowStatusModal] = useState(false);
  const [showProofModal, setShowProofModal] = useState(false);
  const [showWorkOrderModal, setShowWorkOrderModal] = useState(false);
  const [showApproveModal, setShowApproveModal] = useState(false);
  const [newStatus, setNewStatus] = useState('');
  const [statusNote, setStatusNote] = useState('');
  const [selectedProof, setSelectedProof] = useState(null);
  const [approverName, setApproverName] = useState('');
  const [approvalNotes, setApprovalNotes] = useState('');
  const [selectedDepartments, setSelectedDepartments] = useState([]);

  const ORDER_STATUSES = [
    { value: 'new', label: 'New' },
    { value: 'waiting_artwork', label: 'Waiting Artwork' },
    { value: 'proof_sent', label: 'Proof Sent' },
    { value: 'approved', label: 'Approved' },
    { value: 'in_production', label: 'In Production' },
    { value: 'ready', label: 'Ready' },
    { value: 'shipped', label: 'Shipped' },
    { value: 'closed', label: 'Closed' },
  ];

  const DEPARTMENTS = [
    { value: 'art', label: 'Art Department' },
    { value: 'engraving', label: 'Engraving' },
    { value: 'printing', label: 'Printing' },
    { value: 'large_format', label: 'Large Format' },
    { value: 'laser', label: 'Laser' },
    { value: 'shipping', label: 'Shipping' },
  ];

  // Import ops API dynamically
  const { opsAPI } = require('../utils/api');

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      const ordersData = await adminAPI.getOrders({ search: orderId, limit: 1 });
      const orderData = ordersData.orders?.find(o => o.order_id === orderId || o.id === orderId);
      if (orderData) {
        setOrder(orderData);
        setNewStatus(orderData.status);
        const proofsData = await opsAPI.getProofs(orderId);
        setProofs(proofsData.proofs || []);
        const woData = await opsAPI.getWorkOrders({ order_id: orderId });
        setWorkOrders(woData.work_orders || []);
      }
    } catch (err) {
      console.error('Error:', err);
    } finally {
      setLoading(false);
    }
  }, [orderId, opsAPI]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const canMoveToProduction = () => proofs.some(p => p.status === 'approved');

  const updateOrderStatus = async () => {
    try {
      setUpdating(true);
      await opsAPI.updateOrderStatus(orderId, { status: newStatus, note: statusNote });
      setShowStatusModal(false);
      fetchData();
    } catch (err) { alert('Failed: ' + err.message); } finally { setUpdating(false); }
  };

  const createNewProof = async () => {
    try {
      setUpdating(true);
      await opsAPI.createProof(orderId);
      setShowProofModal(false);
      fetchData();
    } catch (err) { alert('Failed: ' + err.message); } finally { setUpdating(false); }
  };

  const sendProof = async (proofId) => {
    try {
      setUpdating(true);
      await opsAPI.sendProof(orderId, proofId);
      fetchData();
    } catch (err) { alert('Failed: ' + err.message); } finally { setUpdating(false); }
  };

  const approveProof = async () => {
    if (!selectedProof || !approverName) return;
    try {
      setUpdating(true);
      await opsAPI.approveProof(orderId, selectedProof.id, { approved_by_name: approverName, approval_notes: approvalNotes });
      setShowApproveModal(false);
      setSelectedProof(null);
      setApproverName('');
      fetchData();
    } catch (err) { alert('Failed: ' + err.message); } finally { setUpdating(false); }
  };

  const generateWorkOrders = async () => {
    if (!selectedDepartments.length) return;
    try {
      setUpdating(true);
      await opsAPI.generateWorkOrders(orderId, selectedDepartments);
      setShowWorkOrderModal(false);
      setSelectedDepartments([]);
      fetchData();
    } catch (err) { alert('Failed: ' + err.message); } finally { setUpdating(false); }
  };

  if (loading) return <div className="flex items-center justify-center py-20"><Loader2 className="w-8 h-8 animate-spin" /></div>;
  if (!order) return <div className="text-center py-12"><p>Order not found</p><Button onClick={onBack}>Back</Button></div>;

  return (
    <div className="space-y-6" data-testid="order-details-inline">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Button variant="ghost" onClick={onBack}><ChevronRight className="w-4 h-4 rotate-180" /></Button>
          <div>
            <h2 className="text-lg font-semibold">Order #{order.order_id || order.id}</h2>
            <p className="text-sm text-gray-500">Created {new Date(order.created_at).toLocaleString()}</p>
          </div>
          <span className={`px-2 py-1 text-xs rounded-full ${
            order.status === 'closed' ? 'bg-green-100 text-green-700' :
            order.status === 'in_production' ? 'bg-purple-100 text-purple-700' :
            order.status === 'approved' ? 'bg-teal-100 text-teal-700' : 'bg-gray-100'
          }`}>{order.status?.replace('_', ' ')}</span>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={() => setShowStatusModal(true)}>Change Status</Button>
          <Button onClick={() => setShowWorkOrderModal(true)}>Generate Work Orders</Button>
        </div>
      </div>

      <div className="grid grid-cols-3 gap-6">
        <div className="col-span-2 space-y-6">
          {/* Items */}
          <div className="bg-white rounded-lg border p-4">
            <h3 className="font-semibold mb-3">Order Items ({order.items?.length || 0})</h3>
            <table className="w-full text-sm">
              <thead className="text-gray-500 border-b"><tr><th className="text-left pb-2">Item</th><th className="text-center pb-2">Qty</th><th className="text-right pb-2">Price</th><th className="text-right pb-2">Total</th></tr></thead>
              <tbody>
                {(order.items || []).map((item, i) => (
                  <tr key={i} className="border-b last:border-0">
                    <td className="py-2">{item.name || item.product_name}</td>
                    <td className="text-center py-2">{item.quantity}</td>
                    <td className="text-right py-2">${(item.price || item.unit_price || 0).toFixed(2)}</td>
                    <td className="text-right py-2 font-medium">${((item.price || item.unit_price || 0) * item.quantity).toFixed(2)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
            <div className="mt-4 pt-4 border-t text-right font-bold">Total: ${(order.total || 0).toFixed(2)}</div>
          </div>

          {/* Proofs */}
          <div className="bg-white rounded-lg border p-4">
            <div className="flex items-center justify-between mb-3">
              <h3 className="font-semibold">Artwork Proofs ({proofs.length})</h3>
              <Button size="sm" onClick={() => setShowProofModal(true)}><Plus className="w-4 h-4 mr-1" />New Proof</Button>
            </div>
            {proofs.length === 0 ? (
              <p className="text-gray-500 text-center py-4">No proofs yet</p>
            ) : (
              <div className="space-y-2">
                {proofs.map(p => (
                  <div key={p.id} className="flex items-center justify-between p-2 border rounded">
                    <span>Version {p.version_number} - <span className={`px-1 rounded ${p.status==='approved'?'bg-green-100':p.status==='sent'?'bg-blue-100':'bg-gray-100'}`}>{p.status}</span></span>
                    {p.status === 'draft' && <Button size="sm" variant="outline" onClick={() => sendProof(p.id)}>Send</Button>}
                    {p.status === 'sent' && <Button size="sm" onClick={() => {setSelectedProof(p); setShowApproveModal(true);}}>Approve</Button>}
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Work Orders */}
          <div className="bg-white rounded-lg border p-4">
            <h3 className="font-semibold mb-3">Work Orders ({workOrders.length})</h3>
            {workOrders.length === 0 ? (
              <p className="text-gray-500 text-center py-4">No work orders</p>
            ) : (
              <div className="space-y-2">
                {workOrders.map(wo => (
                  <div key={wo.id} className="flex items-center justify-between p-2 border rounded">
                    <span className="font-mono text-sm">{wo.work_order_number} - {wo.department}</span>
                    <span className={`px-2 py-0.5 rounded text-xs ${wo.status==='completed'?'bg-green-100':'bg-gray-100'}`}>{wo.status}</span>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        <div className="space-y-4">
          <div className="bg-white rounded-lg border p-4">
            <h3 className="font-semibold mb-3">Customer</h3>
            <p>{order.customer_name}</p>
            <p className="text-sm text-gray-500">{order.customer_email}</p>
          </div>
        </div>
      </div>

      {/* Modals */}
      {showStatusModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-96">
            <h3 className="font-semibold mb-4">Change Status</h3>
            <select value={newStatus} onChange={e => setNewStatus(e.target.value)} className="w-full border rounded p-2 mb-4">
              {ORDER_STATUSES.map(s => <option key={s.value} value={s.value} disabled={s.value==='in_production'&&!canMoveToProduction()}>{s.label}</option>)}
            </select>
            <textarea value={statusNote} onChange={e => setStatusNote(e.target.value)} placeholder="Note..." className="w-full border rounded p-2 mb-4" rows={2} />
            <div className="flex gap-2 justify-end">
              <Button variant="outline" onClick={() => setShowStatusModal(false)}>Cancel</Button>
              <Button onClick={updateOrderStatus} disabled={updating}>Update</Button>
            </div>
          </div>
        </div>
      )}

      {showProofModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-96">
            <h3 className="font-semibold mb-4">Create New Proof</h3>
            <p className="text-sm text-gray-600 mb-4">This will create proof version #{proofs.length + 1}.</p>
            <div className="flex gap-2 justify-end">
              <Button variant="outline" onClick={() => setShowProofModal(false)}>Cancel</Button>
              <Button onClick={createNewProof} disabled={updating}>Create</Button>
            </div>
          </div>
        </div>
      )}

      {showApproveModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-96">
            <h3 className="font-semibold mb-4">Approve Proof</h3>
            <input value={approverName} onChange={e => setApproverName(e.target.value)} placeholder="Approved by (name)..." className="w-full border rounded p-2 mb-4" />
            <textarea value={approvalNotes} onChange={e => setApprovalNotes(e.target.value)} placeholder="Notes..." className="w-full border rounded p-2 mb-4" rows={2} />
            <div className="flex gap-2 justify-end">
              <Button variant="outline" onClick={() => setShowApproveModal(false)}>Cancel</Button>
              <Button onClick={approveProof} disabled={updating || !approverName}>Approve</Button>
            </div>
          </div>
        </div>
      )}

      {showWorkOrderModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-96">
            <h3 className="font-semibold mb-4">Generate Work Orders</h3>
            <div className="space-y-2 mb-4">
              {DEPARTMENTS.map(d => {
                const exists = workOrders.find(wo => wo.department === d.value);
                return (
                  <label key={d.value} className={`flex items-center gap-2 p-2 border rounded cursor-pointer ${exists ? 'opacity-50' : ''}`}>
                    <input type="checkbox" checked={selectedDepartments.includes(d.value)} onChange={() => setSelectedDepartments(prev => prev.includes(d.value) ? prev.filter(x => x !== d.value) : [...prev, d.value])} disabled={!!exists} />
                    {d.label} {exists && '(exists)'}
                  </label>
                );
              })}
            </div>
            <div className="flex gap-2 justify-end">
              <Button variant="outline" onClick={() => setShowWorkOrderModal(false)}>Cancel</Button>
              <Button onClick={generateWorkOrders} disabled={updating || !selectedDepartments.length}>Generate</Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// ==============================================
// ORDERS COMPONENT
// ==============================================
const AdminOrders = ({ onViewOrder }) => {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [statusFilter, setStatusFilter] = useState('');
  const [viewMode, setViewMode] = useState('list'); // 'list' or 'detail'
  const [detailOrderId, setDetailOrderId] = useState(null);

  useEffect(() => {
    const fetchOrders = async () => {
      try {
        const data = await adminAPI.getOrders();
        setOrders(data.orders || []);
      } catch (err) {
        console.error('Error fetching orders:', err);
      } finally {
        setLoading(false);
      }
    };
    fetchOrders();
  }, []);

  const updateOrderStatus = async (orderId, newStatus) => {
    try {
      const response = await adminAPI.updateOrder(orderId, { status: newStatus });
      setOrders(orders.map(o => o.id === orderId ? { ...o, status: newStatus } : o));
      if (selectedOrder?.id === orderId) {
        setSelectedOrder({ ...selectedOrder, status: newStatus });
      }
    } catch (err) {
      console.error('Error updating order:', err);
      alert('Failed to update order status');
    }
  };

  const filteredOrders = statusFilter 
    ? orders.filter(o => o.ops_status === statusFilter || o.status === statusFilter)
    : orders;

  // Effect to handle navigation to detail page via onViewOrder prop
  useEffect(() => {
    if (viewMode === 'detail' && detailOrderId && onViewOrder) {
      onViewOrder(detailOrderId);
      setViewMode('list');
      setDetailOrderId(null);
    }
  }, [viewMode, detailOrderId, onViewOrder]);

  // If showing inline detail view (when onViewOrder is not provided)
  if (viewMode === 'detail' && detailOrderId && !onViewOrder) {
    return (
      <AdminOrderDetailsInline 
        orderId={detailOrderId} 
        onBack={() => { setViewMode('list'); setDetailOrderId(null); }}
      />
    );
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <Loader2 className="w-8 h-8 animate-spin text-gold-500" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-lg font-heading font-semibold text-ivory-800">Orders</h2>
          <p className="text-sm text-ivory-500 font-body">{orders.length} total orders</p>
        </div>
        <select
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value)}
          className="px-3 py-2 rounded-lg border border-ivory-300 bg-white text-ivory-800 font-body text-sm"
        >
          <option value="">All Statuses</option>
          <option value="OPEN">Open</option>
          <option value="IN_PROGRESS">In Progress</option>
          <option value="COMPLETED">Completed</option>
          <option value="CANCELLED">Cancelled</option>
        </select>
      </div>

      {/* Orders Table */}
      <div className="bg-white rounded-xl border border-ivory-200 overflow-hidden">
        <table className="w-full">
          <thead className="bg-ivory-50">
            <tr>
              <th className="px-4 py-3 text-left text-xs font-medium text-ivory-600 uppercase font-body">Order ID</th>
              <th className="px-4 py-3 text-left text-xs font-medium text-ivory-600 uppercase font-body">Customer</th>
              <th className="px-4 py-3 text-left text-xs font-medium text-ivory-600 uppercase font-body">Total</th>
              <th className="px-4 py-3 text-left text-xs font-medium text-ivory-600 uppercase font-body">Status</th>
              <th className="px-4 py-3 text-left text-xs font-medium text-ivory-600 uppercase font-body">Date</th>
              <th className="px-4 py-3 text-left text-xs font-medium text-ivory-600 uppercase font-body">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-ivory-100">
            {filteredOrders.map((order) => (
              <tr key={order.id || order.order_id} className="hover:bg-ivory-50">
                <td className="px-4 py-3 text-sm font-mono text-ivory-600">
                  {order.order_id || order.id?.slice(-8)}
                </td>
                <td className="px-4 py-3 text-sm text-ivory-800">{order.customer_name || order.email}</td>
                <td className="px-4 py-3 text-sm font-medium text-ivory-800">${order.total?.toFixed(2)}</td>
                <td className="px-4 py-3">
                  <span className={`px-2 py-1 text-xs rounded-full ${
                    order.status === 'closed' || order.status === 'delivered' ? 'bg-green-100 text-green-700' :
                    order.status === 'shipped' || order.status === 'ready' ? 'bg-blue-100 text-blue-700' :
                    order.status === 'in_production' ? 'bg-purple-100 text-purple-700' :
                    order.status === 'approved' ? 'bg-teal-100 text-teal-700' :
                    order.status === 'proof_sent' ? 'bg-indigo-100 text-indigo-700' :
                    order.status === 'waiting_artwork' ? 'bg-yellow-100 text-yellow-700' :
                    order.status === 'cancelled' ? 'bg-red-100 text-red-700' :
                    'bg-ivory-200 text-ivory-600'
                  }`}>
                    {order.status?.replace('_', ' ')}
                  </span>
                </td>
                <td className="px-4 py-3 text-sm text-ivory-600">
                  {new Date(order.created_at).toLocaleDateString()}
                </td>
                <td className="px-4 py-3 space-x-1">
                  <Button 
                    variant="ghost" 
                    size="sm"
                    onClick={() => setSelectedOrder(order)}
                    title="Quick View"
                  >
                    <Eye className="w-4 h-4" />
                  </Button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Order Detail Modal */}
      {selectedOrder && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
            <div className="p-6 border-b border-ivory-200 flex items-center justify-between">
              <h3 className="text-lg font-heading font-semibold">Order Details</h3>
              <button onClick={() => setSelectedOrder(null)}>
                <X className="w-5 h-5 text-ivory-500" />
              </button>
            </div>
            <div className="p-6 space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="text-xs text-ivory-500 font-body">Order ID</p>
                  <p className="font-mono text-ivory-800">{selectedOrder.id}</p>
                </div>
                <div>
                  <p className="text-xs text-ivory-500 font-body">Status</p>
                  <select
                    value={selectedOrder.status}
                    onChange={(e) => updateOrderStatus(selectedOrder.id, e.target.value)}
                    className="px-2 py-1 rounded border border-ivory-300 text-sm"
                  >
                    <option value="pending">Pending</option>
                    <option value="processing">Processing</option>
                    <option value="shipped">Shipped</option>
                    <option value="delivered">Delivered</option>
                    <option value="cancelled">Cancelled</option>
                  </select>
                </div>
                <div>
                  <p className="text-xs text-ivory-500 font-body">Customer</p>
                  <p className="text-ivory-800">{selectedOrder.customer_name}</p>
                </div>
                <div>
                  <p className="text-xs text-ivory-500 font-body">Email</p>
                  <p className="text-ivory-800">{selectedOrder.email}</p>
                </div>
              </div>
              <div>
                <p className="text-xs text-ivory-500 font-body mb-2">Items</p>
                <div className="bg-ivory-50 rounded-lg p-3 space-y-2">
                  {selectedOrder.items?.map((item, i) => (
                    <div key={i} className="flex justify-between text-sm">
                      <span>{item.name} x{item.quantity}</span>
                      <span className="font-medium">${(item.price * item.quantity).toFixed(2)}</span>
                    </div>
                  ))}
                </div>
              </div>
              <div className="border-t border-ivory-200 pt-4">
                <div className="flex justify-between font-semibold">
                  <span>Total</span>
                  <span className="text-gold-600">${selectedOrder.total?.toFixed(2)}</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// ==============================================
// CONTACTS COMPONENT
// ==============================================
const AdminContacts = () => {
  const [contacts, setContacts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedContact, setSelectedContact] = useState(null);

  useEffect(() => {
    const fetchContacts = async () => {
      try {
        const data = await adminAPI.getContacts();
        setContacts(data.contacts || []);
      } catch (err) {
        console.error('Error fetching contacts:', err);
      } finally {
        setLoading(false);
      }
    };
    fetchContacts();
  }, []);

  const markAsRead = async (contactId) => {
    try {
      await adminAPI.updateContact(contactId, { status: 'read' });
      setContacts(contacts.map(c => c.id === contactId ? { ...c, status: 'read' } : c));
    } catch (err) {
      console.error('Error marking contact as read:', err);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <Loader2 className="w-8 h-8 animate-spin text-gold-500" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-lg font-heading font-semibold text-ivory-800">Contact Submissions</h2>
        <p className="text-sm text-ivory-500 font-body">{contacts.length} submissions</p>
      </div>

      <div className="bg-white rounded-xl border border-ivory-200 divide-y divide-ivory-100">
        {contacts.length === 0 ? (
          <div className="p-8 text-center text-ivory-500">No contact submissions yet</div>
        ) : (
          contacts.map((contact) => (
            <div 
              key={contact.id} 
              className={`p-4 hover:bg-ivory-50 cursor-pointer ${contact.status !== 'read' ? 'bg-gold-50/30' : ''}`}
              onClick={() => { setSelectedContact(contact); markAsRead(contact.id); }}
            >
              <div className="flex items-start justify-between">
                <div>
                  <p className="font-medium text-ivory-800">{contact.name}</p>
                  <p className="text-sm text-ivory-600">{contact.email}</p>
                  <p className="text-sm text-ivory-500 mt-1 line-clamp-2">{contact.message}</p>
                </div>
                <div className="text-xs text-ivory-400">
                  {new Date(contact.created_at).toLocaleDateString()}
                </div>
              </div>
            </div>
          ))
        )}
      </div>

      {/* Contact Detail Modal */}
      {selectedContact && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl w-full max-w-lg">
            <div className="p-6 border-b border-ivory-200 flex items-center justify-between">
              <h3 className="text-lg font-heading font-semibold">Contact Details</h3>
              <button onClick={() => setSelectedContact(null)}>
                <X className="w-5 h-5 text-ivory-500" />
              </button>
            </div>
            <div className="p-6 space-y-4">
              <div>
                <p className="text-xs text-ivory-500 font-body">Name</p>
                <p className="text-ivory-800">{selectedContact.name}</p>
              </div>
              <div>
                <p className="text-xs text-ivory-500 font-body">Email</p>
                <p className="text-ivory-800">{selectedContact.email}</p>
              </div>
              <div>
                <p className="text-xs text-ivory-500 font-body">Subject</p>
                <p className="text-ivory-800">{selectedContact.subject || '-'}</p>
              </div>
              <div>
                <p className="text-xs text-ivory-500 font-body">Message</p>
                <p className="text-ivory-800 whitespace-pre-wrap">{selectedContact.message}</p>
              </div>
              <div>
                <p className="text-xs text-ivory-500 font-body">Received</p>
                <p className="text-ivory-800">{new Date(selectedContact.created_at).toLocaleString()}</p>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// ==============================================
// USERS COMPONENT
// ==============================================
const AdminUsers = () => {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [updating, setUpdating] = useState(null); // user_id being updated

  const fetchUsers = async () => {
    try {
      const data = await adminAPI.getUsers();
      setUsers(data.users || []);
    } catch (err) {
      console.error('Error fetching users:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchUsers();
  }, []);

  // Toggle allow_pay_later for a user
  const togglePayLater = async (userId, currentValue) => {
    setUpdating(userId);
    try {
      await adminAPI.updateUser(userId, { allow_pay_later: !currentValue });
      // Update local state
      setUsers(prev => prev.map(u => 
        u.user_id === userId ? { ...u, allow_pay_later: !currentValue } : u
      ));
    } catch (err) {
      console.error('Error updating user:', err);
      alert('Failed to update user setting');
    } finally {
      setUpdating(null);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <Loader2 className="w-8 h-8 animate-spin text-gold-500" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-lg font-heading font-semibold text-ivory-800">Users</h2>
        <p className="text-sm text-ivory-500 font-body">{users.length} registered users</p>
      </div>

      <div className="bg-white rounded-xl border border-ivory-200 overflow-hidden">
        <table className="w-full">
          <thead className="bg-ivory-50">
            <tr>
              <th className="px-4 py-3 text-left text-xs font-medium text-ivory-600 uppercase font-body">User</th>
              <th className="px-4 py-3 text-left text-xs font-medium text-ivory-600 uppercase font-body">Email</th>
              <th className="px-4 py-3 text-left text-xs font-medium text-ivory-600 uppercase font-body">Role</th>
              <th className="px-4 py-3 text-left text-xs font-medium text-ivory-600 uppercase font-body">Pay Later</th>
              <th className="px-4 py-3 text-left text-xs font-medium text-ivory-600 uppercase font-body">Joined</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-ivory-100">
            {users.map((user) => (
              <tr key={user.user_id || user.id} className="hover:bg-ivory-50">
                <td className="px-4 py-3">
                  <div className="flex items-center gap-3">
                    <div className="w-8 h-8 rounded-full bg-gold-100 flex items-center justify-center">
                      <span className="text-gold-600 font-medium text-sm">
                        {user.name?.charAt(0) || user.email?.charAt(0).toUpperCase()}
                      </span>
                    </div>
                    <span className="text-sm text-ivory-800">{user.name || 'N/A'}</span>
                  </div>
                </td>
                <td className="px-4 py-3 text-sm text-ivory-600">{user.email}</td>
                <td className="px-4 py-3">
                  <span className={`px-2 py-1 text-xs rounded-full ${
                    user.role === 'admin' ? 'bg-purple-100 text-purple-700' : 'bg-ivory-200 text-ivory-600'
                  }`}>
                    {user.role || 'customer'}
                  </span>
                </td>
                <td className="px-4 py-3">
                  <button
                    onClick={() => togglePayLater(user.user_id, user.allow_pay_later)}
                    disabled={updating === user.user_id}
                    className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-gold-500 focus:ring-offset-2 ${
                      user.allow_pay_later ? 'bg-gold-500' : 'bg-ivory-300'
                    } ${updating === user.user_id ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}`}
                    data-testid={`pay-later-toggle-${user.user_id}`}
                    title={user.allow_pay_later ? 'Pay Later enabled - click to disable' : 'Pay Later disabled - click to enable'}
                  >
                    <span
                      className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                        user.allow_pay_later ? 'translate-x-6' : 'translate-x-1'
                      }`}
                    />
                    {updating === user.user_id && (
                      <Loader2 className="absolute inset-0 m-auto w-4 h-4 animate-spin text-white" />
                    )}
                  </button>
                </td>
                <td className="px-4 py-3 text-sm text-ivory-600">
                  {new Date(user.created_at).toLocaleDateString()}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      
      {/* Legend */}
      <div className="flex items-center gap-4 text-xs text-ivory-500 font-body">
        <div className="flex items-center gap-2">
          <div className="w-6 h-3 rounded-full bg-gold-500"></div>
          <span>Pay Later Enabled</span>
        </div>
        <div className="flex items-center gap-2">
          <div className="w-6 h-3 rounded-full bg-ivory-300"></div>
          <span>Pay Later Disabled</span>
        </div>
      </div>
    </div>
  );
};

export default AdminDashboard;
